import { useMemo } from 'react'
import {
	abilityFromPacked,
	type Action,
	type AppAbility,
	type Grants,
	grantsSection,
	type PackedRules,
	type Resource,
} from '@rentflowdev/access'
import { useGetAccessQuery } from '@/api'

/** What a menu item, a route or a button needs: any one of the actions. */
export interface Requirement {
	action: Action | readonly Action[]
	resource: Resource
}

export type AccessState =
	| { status: 'loading' }
	// The rules could not be loaded: the cabinet shows everything and the
	// gateway refuses what the role does not allow, as before.
	| { status: 'unknown' }
	// grants is absent from a gateway older than it: sections then go by the
	// rules alone.
	| { status: 'ready'; ability: AppAbility; grants?: Grants }

// A role changed by the owner or the platform's super admin reaches an open
// tab within this long.
const ACCESS_REFRESH_MS = 2 * 60 * 1000

/**
 * The caller's rules from the role catalog — the same the gateway checks on
 * every request — and their role's grants. The menu, the routes and the
 * buttons follow them so a member does not open what would only answer 403;
 * the backend still decides.
 */
export function useAccess(): AccessState {
	const { data, isError } = useGetAccessQuery(undefined, { pollingInterval: ACCESS_REFRESH_MS })
	return useMemo<AccessState>(() => {
		if (data) {
			return {
				status: 'ready',
				ability: abilityFromPacked(data.rules as PackedRules),
				grants: data.grants as Grants | undefined,
			}
		}
		return isError ? { status: 'unknown' } : { status: 'loading' }
	}, [data, isError])
}

const actionsOf = (requirement: Requirement): Action[] => [requirement.action].flat()

/** Whether the caller may do it at all: for buttons. */
export function allows(access: AccessState, requirement: Requirement | undefined): boolean {
	if (!requirement || access.status === 'unknown') return true
	if (access.status === 'loading') return false
	return actionsOf(requirement).some((action) => access.ability.can(action, requirement.resource))
}

/**
 * Whether the caller's role opens a section: a grant that reaches rows in
 * its own right. The accountant reads the property under a rental they bill,
 * which does not open the properties section (grantsSection).
 */
export function opens(access: AccessState, requirement: Requirement | undefined): boolean {
	if (!allows(access, requirement)) return false
	if (!requirement || access.status !== 'ready' || !access.grants) return true
	const { grants } = access
	return actionsOf(requirement).some((action) => grantsSection(grants, action, requirement.resource))
}

/** Whether the caller may: false while the rules load, so nothing flashes. */
export function useCan(action: Requirement['action'], resource: Resource): boolean {
	const access = useAccess()
	return allows(access, { action, resource })
}
